/**
 * Hook to obtain a gqlFetch function for invoking GraphQL operations.
 *
 * For details on usage, see https://khanacademy.atlassian.net/wiki/spaces/ENG/pages/1927577736/useGql.
 */
import * as React from "react";

import * as wbd from "@khanacademy/wonder-blocks-data";
import type {GqlOperation} from "@khan/data-gql-core";

import {DefaultFastlyQueryScope, DefaultQueryScope} from "./constants.ts";
import {toNormalizedOperation} from "./to-normalized-operation.ts";
import type {GqlFetchWithCacheFn, GqlFetchWithCacheOptions} from "./types.ts";
import {useRequestIdBuilder} from "./use-request-id-builder.ts";

/**
 * Hook to obtain a gqlFetch function for performing GraphQL requests.
 *
 * The fetch function will resolve null if the request was aborted, otherwise
 * it will resolve the data returned by the GraphQL server.
 *
 * It will reject with an error if the request failed or the response was
 * invalid.
 *
 * Finally, note that this only uses local caching for fastly-cacheable queries.
 * All other calls will lead to a network request.
 */
export const useGqlWithCache = (): GqlFetchWithCacheFn => {
    // We get the Wonder Blocks Data function.
    // This already manages default context for us.
    const wbdFetch = wbd.useGql();

    // We need to be able to get a request ID for Fastly-cacheable queries.
    const requestIdBuilder = useRequestIdBuilder();

    // Now we want a wrapper around that, which we only need to update
    // if the Wonder Blocks Data function changes.
    // The webapp version needs to map webapp operations and context
    // to the Wonder Blocks compatible equivalent.
    const gqlFetch = React.useCallback(
        async <
            TData extends Record<any, any>,
            TVariables extends Record<any, any>,
        >(
            operation: GqlOperation<TData, TVariables>,
            {
                variables,
                context,
                fetchPolicy,
            }: GqlFetchWithCacheOptions<TVariables> = Object.freeze({
                variables: undefined,
                context: undefined,
                fetchPolicy: wbd.FetchPolicy.NetworkOnly, // Default to network only
            }),
        ): Promise<TData> => {
            const wbdOperation = toNormalizedOperation(operation);

            const isFastlyCacheable =
                wbdOperation.type === "query" && context?.fastlyCaching != null;
            const isGqlCacheable = wbdOperation.type === "query";

            const requestId = requestIdBuilder(
                operation,
                variables,
                context ?? {},
            );

            if (isFastlyCacheable) {
                const fastlyCachedResult = wbd.SharedCache.get(
                    DefaultFastlyQueryScope,
                    requestId,
                );
                if (fastlyCachedResult != null) {
                    // We assert that the cached value is the same type as what we
                    // want to return.
                    return fastlyCachedResult as TData;
                }
            } else if (isGqlCacheable) {
                if (
                    fetchPolicy === wbd.FetchPolicy.CacheBeforeNetwork ||
                    fetchPolicy === wbd.FetchPolicy.CacheOnly
                ) {
                    // Use the same cache as useQuery
                    const gqlCachedResult = wbd.SharedCache.get(
                        DefaultQueryScope,
                        requestId,
                    );

                    if (gqlCachedResult != null) {
                        return gqlCachedResult as TData;
                    }
                }
            }

            if (fetchPolicy === wbd.FetchPolicy.CacheOnly) {
                throw new wbd.DataError(
                    "Cannot fetch with CacheOnly policy",
                    wbd.DataErrors.NotAllowed,
                );
            }

            const {curriculum, targetLocale, fastlyCaching} = context ?? {};
            const result = await wbdFetch<TData, TVariables>(wbdOperation, {
                variables,
                context: {
                    targetLocale,
                    curriculum,
                    fastlyCaching,
                } as Partial<wbd.GqlContext>,
            });

            // We need to update the appropriate cache.
            if (isFastlyCacheable) {
                wbd.SharedCache.set(DefaultFastlyQueryScope, requestId, result);
            } else if (isGqlCacheable) {
                // Use the same cache as useQuery
                wbd.SharedCache.set(DefaultQueryScope, requestId, result);
            }

            return result;
        },
        [wbdFetch, requestIdBuilder],
    );

    return gqlFetch;
};
